/* @refresh skip */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { Button } from './ui/button';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { trackAdClick } from '@/hooks/useAdTracking';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';

interface Advertisement {
  id: string;
  title: string;
  description: string | null;
  title_translations?: Record<string, string>;
  description_translations?: Record<string, string>;
  image_url: string | null;
  image_urls?: Record<string, string>;
  link_url: string | null;
}

export const AdvertisementBanner = () => {
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [autoplayDelay, setAutoplayDelay] = useState(5000);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  
  // Helper function to get translated text
  const getTranslatedText = (translations: Record<string, string> | undefined, fallback: string) => {
    if (!translations) return fallback;
    
    // For French-based languages (fr, lg-fr, sw), always use French
    // For English, use English
    // This ensures French/Lingala/Swahili users see French ads, English users see English ads
    const targetLang = language === 'en' ? 'en' : 'fr';
    
    // Try target language
    if (translations[targetLang]) return translations[targetLang];
    
    // Fallback to French (primary language)
    if (translations.fr) return translations.fr;
    
    // Fallback to English
    if (translations.en) return translations.en;
    
    // Final fallback
    return fallback;
  };

  // Helper function to get image URL based on language
  const getImageUrl = (ad: Advertisement): string | null => {
    const targetLang = language === 'en' ? 'en' : 'fr';
    
    // If image_urls exists and has an image for the target language, use it
    if (ad.image_urls && ad.image_urls[targetLang]) {
      return ad.image_urls[targetLang];
    }
    
    // Fallback to French image if available
    if (ad.image_urls && ad.image_urls.fr) {
      return ad.image_urls.fr;
    }
    
    // Fallback to old image_url field
    return ad.image_url;
  };
  const plugins = useMemo(() => {
    if (!autoplayEnabled || ads.length < 2) return [];
    return [
      Autoplay({
        delay: autoplayDelay,
        stopOnInteraction: false,
        rootNode: (emblaRoot) => emblaRoot,
      }),
    ];
  }, [autoplayEnabled, autoplayDelay, ads.length]);
  
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true },
    plugins
  );

  useEffect(() => {
    fetchAds();
    fetchBannerSettings();
    
    const adsChannel = supabase
      .channel('advertisements-banner')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'advertisements'
        },
        () => {
          fetchAds();
        }
      )
      .subscribe();

    const settingsChannel = supabase
      .channel('banner-settings')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'key=in.(banner_autoplay_enabled,banner_autoplay_delay)'
        },
        () => {
          fetchBannerSettings();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(adsChannel);
      supabase.removeChannel(settingsChannel);
    };
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const fetchBannerSettings = async () => {
    try {
      const { data: enabledData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'banner_autoplay_enabled')
        .maybeSingle();

      const { data: delayData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'banner_autoplay_delay')
        .maybeSingle();

      if (enabledData?.value !== undefined) {
        const enabled = typeof enabledData.value === 'string' 
          ? JSON.parse(enabledData.value) 
          : enabledData.value;
        setAutoplayEnabled(enabled);
        setIsPlaying(enabled);
      }

      if (delayData?.value !== undefined) {
        const delay = typeof delayData.value === 'string'
          ? JSON.parse(delayData.value)
          : delayData.value;
        setAutoplayDelay(Number(delay) || 5000);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres bannière:', error);
    }
  };

  const fetchAds = async () => {
    try {
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('advertisements')
        .select('*')
        .eq('position', 'top')
        .eq('active', true)
        .lte('start_date', now)
        .or(`end_date.is.null,end_date.gte.${now}`)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Erreur chargement publicités:', error);
        return;
      }

      setAds(data || []);
    } catch (error) {
      console.error('Erreur chargement publicités:', error);
    }
  };

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const toggleAutoplay = useCallback(() => {
    if (!emblaApi || !autoplayEnabled || ads.length === 0) return;
    const autoplay = emblaApi.plugins()?.autoplay;
    if (!autoplay) return;

    if (isPlaying) {
      autoplay.stop();
    } else {
      autoplay.play();
    }
    setIsPlaying(!isPlaying);
  }, [emblaApi, isPlaying, autoplayEnabled, ads.length]);

  useEffect(() => {
    if (!emblaApi || !autoplayEnabled || ads.length === 0) return;
    const autoplay = emblaApi.plugins()?.autoplay;
    if (!autoplay) return;

    if (isPlaying) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [emblaApi, isPlaying, autoplayEnabled, ads.length]);

  const handleAdClick = (ad: Advertisement) => {
    trackAdClick(ad.id, user?.id);
    if (!ad.link_url) {
      navigate(`/promo/${ad.id}`);
      return;
    }
    if (ad.link_url.startsWith('/')) {
      navigate(ad.link_url);
    } else {
      // Open external links in the same tab (no new window)
      window.location.href = ad.link_url;
    }
  };

  if (ads.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 overflow-hidden">
      <div className="embla" ref={emblaRef}>
        <div className="embla__container flex">
          {ads.map((ad) => (
            <div key={ad.id} className="embla__slide flex-[0_0_100%] min-w-0">
              <div 
                className={`relative w-full h-[400px] ${ad.link_url ? 'cursor-pointer' : ''}`}
                onClick={() => handleAdClick(ad)}
              >
                {getImageUrl(ad) ? (
                  <div className="relative w-full h-full">
                    <img
                      src={getImageUrl(ad)!}
                      alt={ad.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent">
                      <div className="container mx-auto px-4 h-full flex items-center">
                        <div className="max-w-2xl text-white">
                          <h2 className="text-5xl font-bold mb-4 drop-shadow-lg">
                            {getTranslatedText(ad.title_translations, ad.title)}
                          </h2>
                          {ad.description && (
                            <p className="text-xl mb-6 drop-shadow-md">
                              {getTranslatedText(ad.description_translations, ad.description)}
                            </p>
                          )}
                          {ad.link_url && (
                            <Button 
                              size="lg"
                              className="bg-white text-blue-700 hover:bg-gray-100 font-semibold"
                              onClick={(e) => { e.stopPropagation(); handleAdClick(ad); }}
                            >
                              Voir l'offre
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700">
                    <div className="container mx-auto px-4">
                      <div className="max-w-3xl mx-auto text-center text-white">
                        <h2 className="text-6xl font-bold mb-6 drop-shadow-lg">
                          {getTranslatedText(ad.title_translations, ad.title)}
                        </h2>
                        {ad.description && (
                          <p className="text-2xl mb-8 drop-shadow-md">
                            {getTranslatedText(ad.description_translations, ad.description)}
                          </p>
                        )}
                        {ad.link_url && (
                          <Button 
                            size="lg"
                            className="bg-white text-blue-700 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
                          >
                            Voir l'offre
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {ads.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full h-12 w-12 shadow-lg z-10"
            onClick={scrollPrev}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full h-12 w-12 shadow-lg z-10"
            onClick={scrollNext}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>

          {/* Bouton rotation automatique */}
          {autoplayEnabled && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 bg-white/80 hover:bg-white text-gray-800 rounded-full shadow-lg z-10 flex items-center gap-2"
              onClick={toggleAutoplay}
            >
              {isPlaying ? (
                <>
                  <Pause className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm">Pause</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm">Lecture</span>
                </>
              )}
            </Button>
          )}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {ads.map((_, index) => (
              <button
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === selectedIndex 
                    ? 'w-8 bg-white' 
                    : 'w-2 bg-white/50 hover:bg-white/75'
                }`}
                onClick={() => emblaApi?.scrollTo(index)}
                aria-label={`Aller à la publicité ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
